import { Component, OnInit } from '@angular/core';
import {
	FormGroup,
	FormControl,
	Validators,
	ReactiveFormsModule,
} from '@angular/forms';

import { DropdownModule } from 'primeng/dropdown';
import { AccordionModule } from 'primeng/accordion';
import { PanelModule } from 'primeng/panel';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import {
	AddressComponent,
	ImageComponent,
	enumToSelectItem,
	ImagesBrokerService,
} from '@biorec/common';

import {
	Doctor,
	PaymentMethod,
	ResultDeliveryMethod,
	ExamDeliveryMethod,
} from '@biorec/partners/domain';
import { DoctorsServiceGql } from '../doctors.service.gql';
import { ImageUploadComponent } from '../../../../common/modules/images/image-upload/image-upload.component';
import { GoogleMapsModule } from '@angular/google-maps';

@Component({
	standalone: true,
	templateUrl: './doctor-info.component.html',
	styleUrls: ['./doctor-info.component.scss'],
	imports: [
		ReactiveFormsModule,
		DropdownModule,
		AccordionModule,
		PanelModule,
		GoogleMapsModule,
		InputSwitchModule,
		CalendarModule,
		InputTextModule,
		InputTextareaModule,
		AddressComponent,
		ImageComponent,
		ImageUploadComponent,
	],
	providers: [ImagesBrokerService],
})
export class DoctorInfoComponent implements OnInit {

	protected specialities: SelectItem[] = [
		{
			label: $localize`Gynecologist`,
			value: 'gynecologist',
		},
	];

	protected examsDeliveryMethod: SelectItem[];
	protected resultsDeliveryMethod: SelectItem[];
	protected paymentMethods: SelectItem[];
	protected customerAllData: FormGroup;
	protected initialFormValue: Doctor;

	constructor(
		private svc: DoctorsServiceGql,
		private confirmationService: ConfirmationService,
		private msgSvc: MessageService
	) {
	}

	ngOnInit() {
		this.svc.currentDoctor$.subscribe(this.prepareForm.bind(this));

		this.paymentMethods = enumToSelectItem(PaymentMethod);
		this.examsDeliveryMethod = enumToSelectItem(ExamDeliveryMethod);
		this.resultsDeliveryMethod = enumToSelectItem(ResultDeliveryMethod);
	}

	private prepareForm(doctor: Doctor) {
		this.initialFormValue = doctor;
		if (!doctor) {
			return;
		}
		this.customerAllData = new FormGroup({
			avatar: new FormControl(doctor.avatar?.id),
			firstName: new FormControl(doctor.firstName, Validators.required),
			lastName: new FormControl(doctor.lastName, Validators.required),
			specialty: new FormControl(doctor.specialty, Validators.required),
			phone: new FormControl(doctor.phone),
			mobile: new FormControl(doctor.mobile),
			fax: new FormControl(doctor.fax),
			email: new FormControl(
				doctor.email,
				Validators.compose([
					Validators.pattern(
						'^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+[.]+[a-zA-Z0-9-.]{2,}$'
					),
				])
			),
			secretaryName: new FormControl(doctor.secretaryName),
			secretaryPhone: new FormControl(doctor.secretaryPhone),
			start1: new FormControl(
				doctor.start1 == null ? null : doctor.start1.slice(0, -3)
			),
			end1: new FormControl(
				doctor.end1 == null ? null : doctor.end1.slice(0, -3)
			),
			start2: new FormControl(
				doctor.start2 == null ? null : doctor.start2.slice(0, -3)
			),
			end2: new FormControl(
				doctor.end2 == null ? null : doctor.end2.slice(0, -3)
			),
			start3: new FormControl(
				doctor.start3 == null ? null : doctor.start3.slice(0, -3)
			),
			end3: new FormControl(
				doctor.end3 == null ? null : doctor.end3.slice(0, -3)
			),
			start4: new FormControl(
				doctor.start4 == null ? null : doctor.start4.slice(0, -3)
			),
			end4: new FormControl(
				doctor.end4 == null ? null : doctor.end4.slice(0, -3)
			),
			start5: new FormControl(
				doctor.start5 == null ? null : doctor.start5.slice(0, -3)
			),
			end5: new FormControl(
				doctor.end5 == null ? null : doctor.end5.slice(0, -3)
			),

			address: new FormControl(
				{
					fullAddress: doctor.fullAddress,
					latitude: doctor.latitude,
					longitude: doctor.longitude,
				},
				Validators.required
			),

			invoiceName: new FormControl(doctor.invoiceName),
			invoiceAddress: new FormControl(doctor.invoiceAddress),
			vatNumber: new FormControl(doctor.vatNumber),
			taxOffice: new FormControl(doctor.taxOffice),

			notificationsEnabled: new FormControl(doctor.notificationsEnabled),
			statisticsEnabled: new FormControl(doctor.statisticsEnabled),
			recommendationsEnabled: new FormControl(
				doctor.recommendationsEnabled
			),
			examsDeliveryMethod: new FormControl(doctor.examsDeliveryMethod),
			examsDeliveryMethodComment: new FormControl(
				doctor.examsDeliveryMethodComment
			),
			resultsDeliveryMethod: new FormControl(
				doctor.resultsDeliveryMethod
			),
			resultsDeliveryMethodComment: new FormControl(
				doctor.resultsDeliveryMethodComment
			),
			paymentMethod: new FormControl(doctor.paymentMethod),
			paymentMethodComment: new FormControl(doctor.paymentMethodComment),
		});
		['avatar'].forEach((prop) => {
			if (this.customerAllData.value[prop]?.id) {
				this.customerAllData.value[prop] =
					this.customerAllData.value[prop].id;
			}
		});
		// if the doctor specialty is not in the list, add it
		if (doctor.specialty && !this.specialities.some(s => s.value == doctor.specialty)) {
			this.specialities.push({
				label: doctor.specialty,
				value: doctor.specialty,
			});
		}
	}


	protected isFormTouched(): boolean {
		return this.customerAllData.dirty;
	}

	protected confirmSaving() {
		this.customerAllData.markAllAsTouched();
		if (this.customerAllData.valid) {
			const value = {
				...this.customerAllData.value,
				...this.customerAllData.value.address,
			};
			delete value.address;

			if (typeof this.customerAllData.value.avatar != 'object') {
				value.avatar = {
					id: this.customerAllData.value.avatar,
				};
			}

			this.svc.updateDoctor(value).subscribe(() =>
				this.msgSvc.add({
					severity: 'success',
					life: 5000,
					summary: $localize`Success`,
					detail: $localize`Your changes have been saved.`,
				})
			);
			this.customerAllData.markAsPristine();
		}
	}

	protected confirmDiscard() {
		if (this.customerAllData.pristine) {
			return;
		}
		this.confirmationService.confirm({
			key: 'global',
			message: $localize`Are you sure you want to discard changes?`,
			acceptLabel: $localize`Yes`,
			accept: () => {
				this.customerAllData.reset(this.initialFormValue);
			},
		});
	}
}
